import React, { useState, useRef } from 'react';
import { TrashIcon, XMarkIcon, EyeIcon, EyeSlashIcon, ArrowLeftIcon, ArrowRightIcon, SparklesIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { Product } from '../data/mockData';
import AIModal from './AIModal';
import { TransformedLayer } from '../utils/resourceUtils';
import { uploadFile, baseImageToOss, generateRandomCode } from '../utils/common';
import { OSS_BUCKET, OSS_BASE_URL } from '../config';
import { getEffectInfo } from '../utils/renderLayer';
import { subscribeAITaskCompleted } from '../utils/aiEvents';



interface AIGenerateResponse {
  code: string;
  src: string;
  status: string;
}

interface DesignElement {
  id: string;
  type: 'text' | 'shape' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  content?: string;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  shape?: string;
  visible: boolean;
}

interface PropertiesPanelProps {
  selectedLayerId: string | null;
  transformedLayers: TransformedLayer[];
  selectedElement: string | null;
  elements: DesignElement[];
  selectedSize: string;
  product: Product;
  onDeleteLayer: (layerId: string) => void;
  onDeselectLayer: () => void;
  onUpdateLayerPosition: (layerId: string, x: number, y: number) => void;
  onUpdateLayerSize: (layerId: string, width: number, height: number) => void;
  onUpdateTransformedLayers: (layers: TransformedLayer[]) => void;
  onToggleElementVisibility: (elementId: string) => void;
  onDeleteElement: (elementId: string) => void;
  onUpdateElementPosition: (elementId: string, x: number, y: number) => void;
  onSelectedSizeChange: (size: string) => void;
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedLayerId,
  transformedLayers,
  selectedElement,
  elements,
  selectedSize,
  product,
  onDeleteLayer,
  onDeselectLayer,
  onUpdateLayerPosition,
  onUpdateLayerSize,
  onUpdateTransformedLayers,
  onToggleElementVisibility,
  onDeleteElement,
  onUpdateElementPosition,
  onSelectedSizeChange
}) => {
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [selectedAICode, setSelectedAICode] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fillUploadRef = useRef<HTMLInputElement>(null);
  const [fillType, setFillType] = useState<'color' | 'image'>('image');
  const [fabricSelection, setFabricSelection] = useState<any | null>(null);
  React.useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {}; 
      setFabricSelection(d);
    };
    window.addEventListener('giftmkr:activeObjectChanged', handler as EventListener);
    return () => window.removeEventListener('giftmkr:activeObjectChanged', handler as EventListener);
  }, []);
  React.useEffect(() => {
    if (fabricSelection) {
      if (typeof fabricSelection.fill === 'string') {
        setFillType('color');
      } else if (fabricSelection?.data?.fillSrc) {
        setFillType('image');
      }
    }
  }, [fabricSelection]);
 

  // 订阅 AI 完成事件，触发刷新
  React.useEffect(() => {
    const unsubscribe = subscribeAITaskCompleted((res) => {  
      console.log("subscribeAITaskCompleted", res);
      // 注意：这里的 selectedLayerId 是闭包中的旧值，如果 selectedLayerId 经常变化，可能拿不到最新的值
      // 建议使用 ref 或者在回调中不依赖 selectedLayerId，或者直接分发事件
      console.log('selectedLayerId (in effect):', selectedLayerId);
      
      // 只要 status 是 completed，就尝试分发事件
      if (res.status === 'completed') {
        const ev = new CustomEvent('giftmkr:updateActiveObject', { detail: { ai: res.code } });
        window.dispatchEvent(ev);
      }
    });
    return () => unsubscribe();
  }, [selectedLayerId]); // 添加依赖，确保回调中能拿到最新的 selectedLayerId


  const normalizeAngle = (n: number) => ((n % 360) + 360) % 360;
  const setAngle = (deg: number) => {
    const norm = normalizeAngle(deg);
    const ev = new CustomEvent('giftmkr:updateActiveObject', { detail: { angle: norm } });
    window.dispatchEvent(ev);
    setFabricSelection((prev: any) => (prev ? { ...prev, angle: norm } : prev));
  };
  const rotateBy = (delta: number) => {
    const current = fabricSelection?.angle || 0;
    setAngle(current + delta);
  };
  const handleAngleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    if (!Number.isFinite(n)) return;
    setAngle(n);
  };

  const handleAIGenerate = (styleModel: string, description: string) => {
    // This function is called after successful AI generation
    console.log('AI generation completed:', { styleModel, description });
  };

  const handleAIGenerated = (data: AIGenerateResponse) => {
    // Use the returned image URL from the AI API
    if (selectedLayerId && data.status === 'completed') {
      const ev = new CustomEvent('giftmkr:updateActiveObject', { detail: { ai: data.code } });
      window.dispatchEvent(ev);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedLayerId) return;

    setIsUploading(true);
    try {
      // 将文件转换为base64
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const base64String = evt.target?.result as string;

        try {
          // 调用 baseImageToOss 上传到OSS，使用实际文件扩展名
          const uploadedName = await baseImageToOss(
            generateRandomCode() + getFileExtension(file.name),
            base64String,
            OSS_BUCKET
          );

          console.log('上传到OSS成功:', uploadedName);

          // 使用OSS返回的URL
          const uploadedUrl = `${OSS_BASE_URL}/${uploadedName}`;

          // 更新选中图层的URL
          onUpdateTransformedLayers(
            transformedLayers.map(layer =>
              layer.id === selectedLayerId
                ? {
                  ...layer,
                  url: uploadedUrl,
                }
                : layer
            )
          );

          console.log('Image uploaded to OSS and applied:', uploadedUrl);
        } catch (uploadError) {
          console.error('上传到OSS失败，回退到原上传接口:', uploadError);
          try {
            const uploadedUrl = await uploadFile(file);
            console.log('Fallback upload successful:', uploadedUrl);
            onUpdateTransformedLayers(
              transformedLayers.map(layer =>
                layer.id === selectedLayerId
                  ? {
                    ...layer,
                    url: uploadedUrl,
                  }
                  : layer
              )
            );
          } catch (fallbackError) {
            console.error('Fallback upload failed:', fallbackError);
            alert('Upload failed. Please try again.');
          }
        } finally {
          setIsUploading(false);
          // 清理文件输入
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('文件处理失败:', error);
      setIsUploading(false);
      alert('File processing failed. Please try again.');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const selectedLayerUrlForModal = selectedLayerId
    ? (transformedLayers.find(l => l.id === selectedLayerId)?.src || '')
    : ''; 
  console.log('selectedLayerId:', selectedLayerId);
  console.log('selectedLayerUrl:', selectedLayerUrlForModal);

  return (
    <div className="w-80 bg-gray-50 border-l border-gray-300 flex flex-col h-full">
      <div className="p-4 flex-1 overflow-y-auto">
        <h3 className="font-semibold mb-4 text-sm">Properties</h3>

        {/* 选中图层的属性 */}
        {fabricSelection && (
          (() => {
            const selectedLayer = fabricSelection;
            if (!selectedLayer) return null;  
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Layer Properties</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const ev = new CustomEvent('giftmkr:deleteActiveObject');
                        window.dispatchEvent(ev);
                      }}
                      className="p-1 hover:bg-gray-100 rounded text-red-600"
                      title="Delete Layer"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const ev = new CustomEvent('giftmkr:deselect');
                        window.dispatchEvent(ev);
                      }}
                      className="p-1 hover:bg-gray-100 rounded text-gray-600"
                      title="Deselect Layer"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>


                {/* Text Layer Properties */}
                {selectedLayer.type === 'IText' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Text Content</label>
                      <textarea
                        value={(fabricSelection?.text ?? selectedLayer.text) || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const ev = new CustomEvent('giftmkr:updateActiveObject', { detail: { text: val } });
                          window.dispatchEvent(ev);
                          setFabricSelection((prev: any) => (prev ? { ...prev, text: val } : prev));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-2">Font Size</label>
                        <input
                          type="number"
                          value={fabricSelection?.fontSize ?? 16}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            if (!Number.isFinite(n)) return;
                            const ev = new CustomEvent('giftmkr:updateActiveObject', { detail: { fontSize: n } });
                            window.dispatchEvent(ev);
                            setFabricSelection((prev: any) => (prev ? { ...prev, fontSize: n } : prev));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Font Family</label>
                        <select
                          value={selectedLayer.fontFamily || 'Arial'}
                          onChange={(e) => {
                            const ev = new CustomEvent('giftmkr:updateActiveObject', { detail: { fontFamily: e.target.value } });
                            window.dispatchEvent(ev);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="Arial">Arial</option>
                          <option value="Helvetica">Helvetica</option>
                          <option value="Times New Roman">Times New Roman</option>
                          <option value="Georgia">Georgia</option>
                          <option value="Verdana">Verdana</option>
                          <option value="Courier New">Courier New</option>
                        </select>
                      </div>
                    </div>

                  </div>
                )}

                {/* Image Layer Properties */}
                {selectedLayer.type === 'Image' && (
                  <div className="space-y-4"> 
                    {/* AI Enhancement Button for Image Layers */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <SparklesIcon className="w-5 h-5 text-purple-600" />
                        <span className="text-sm font-medium text-gray-700">AI Enhancement </span>
                      </div>
                      {/* AI Value Tag */}
                      {selectedLayer.ai && (
                        <div className="flex items-center justify-between p-2 bg-purple-50 border border-purple-200 rounded-lg">
                          <span className="text-sm text-purple-700 truncate flex-1">
                            AI: {selectedLayer.ai}
                          </span>
                          <button
                            onClick={() => {
                              onUpdateTransformedLayers(
                                transformedLayers.map(layer =>
                                  layer.id === selectedLayerId
                                    ? {
                                      ...layer,
                                      ai: undefined
                                    }
                                    : layer
                                )
                              );
                            }}
                            className="ml-2 p-1 text-purple-500 hover:text-purple-700 hover:bg-purple-100 rounded transition-colors"
                            title="Remove AI"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      <button
                        onClick={() => {
                          // 如果 layer.ai 存在，传递 AI 代码
                          const aiCode = selectedLayer.ai;
                          setIsAIModalOpen(true);
                          // 将 AI 代码传递给 AIModal
                          setSelectedAICode(aiCode);
                        }}
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center justify-center space-x-2"
                      >
                        <SparklesIcon className="w-4 h-4" />
                        <span>Generate with AI</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Frame Layer Properties */}
                {selectedLayer.type === 'frame' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Frame Style</label>
                      <select
                        value={selectedLayer.options.frameStyle || 'square'}
                        onChange={(e) => {
                          onUpdateTransformedLayers(
                            transformedLayers.map(layer =>
                              layer.id === selectedLayerId
                                ? {
                                  ...layer,

                                  frameStyle: e.target.value
                                }
                                : layer
                            )
                          );
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="square">Square</option>
                        <option value="circle">Circle</option>
                        <option value="diamond">Diamond</option>
                        <option value="rounded">Rounded</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Frame Color</label>
                      <input
                        type="color"
                        value={selectedLayer.options.fill || '#000000'}
                        onChange={(e) => {
                          onUpdateTransformedLayers(
                            transformedLayers.map(layer =>
                              layer.id === selectedLayerId
                                ? {
                                  ...layer,

                                  fill: e.target.value
                                }
                                : layer
                            )
                          );
                        }}
                        className="w-full h-10 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Frame Size</label>
                      <input
                        type="number"
                        value={selectedLayer.options.frameSize || 10}
                        onChange={(e) => {
                          onUpdateTransformedLayers(
                            transformedLayers.map(layer =>
                              layer.id === selectedLayerId
                                ? {
                                  ...layer,
                                  frameSize: Number(e.target.value)

                                }
                                : layer
                            )
                          );
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        min="1"
                        max="50"
                      />
                    </div>

                  </div>
                )}

                {/* Shape Layer Properties */}
                {selectedLayer.type !== 'Image' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-2">Fill in Color/Pattern</label>
                      <div className="flex items-center space-x-2 mb-2">
                        <button
                          type="button"
                          className={`px-3 py-1 rounded border ${fillType === 'color' ? 'bg-gray-200 border-gray-400' : 'bg-white border-gray-300'}`}
                          onClick={() => setFillType('color')}
                        >
                          Color
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 rounded border ${fillType === 'image' ? 'bg-gray-200 border-gray-400' : 'bg-white border-gray-300'}`}
                          onClick={() => setFillType('image')}
                        >
                          Image
                        </button>
                      </div>
                      {fillType === 'color' ? (
                        <input
                          type="color"
                          className="w-12 h-12 border border-gray-300 rounded"
                          value={typeof fabricSelection?.fill === 'string' ? fabricSelection.fill : '#000000'}
                          onChange={(e) => {
                            const c = e.target.value;
                            window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail: { fill: c } }));
                            setFabricSelection((prev: any) => (prev ? { ...prev, fill: c } : prev));
                          }}
                        />
                      ) : (
                        <div className="flex flex-col space-y-2">
                          <label className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded cursor-pointer transition-colors">
                            {fabricSelection?.data?.fillSrc ? (
                              <img
                                src={fabricSelection.data.fillSrc}
                                alt="thumbnail"
                                className="w-16 h-16 rounded border object-cover"
                              />
                            ) : (
                              <>
                                <PhotoIcon className="w-5 h-5 mr-2" />
                                <span className="text-sm">Upload Image</span>
                              </>
                            )}
                            <input
                              ref={fillUploadRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                let finalUrl = '';
                                try {
                                  const reader = new FileReader();
                                  const base64: string = await new Promise((resolve, reject) => {
                                    reader.onload = () => resolve(String(reader.result));
                                    reader.onerror = reject;
                                    reader.readAsDataURL(file);
                                  });
                                  const code = generateRandomCode();
                                  const key = await baseImageToOss(code + '.png', base64, OSS_BUCKET as string);
                                  finalUrl = `${OSS_BASE_URL}${key}`;
                                } catch { }
                                if (!finalUrl) {
                                  try {
                                    finalUrl = await uploadFile(file);
                                  } catch { }
                                }
                                if (!finalUrl) return;
                                const urlWithStyle = finalUrl + "?x-oss-process=style/large";
                                window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail: { fillImageUrl: urlWithStyle, patternRepeat: 'no-repeat', fillMode: 'short' } }));
                                setFillType('image');
                                setFabricSelection((prev: any) => prev ? { ...prev, data: { ...(prev.data || {}), fillSrc: urlWithStyle } } : prev);
                                if (fillUploadRef.current) fillUploadRef.current.value = '';
                              }}
                            />
                          </label>
                          <span className="text-xs text-gray-500">Supports image tiling fill for shapes</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Border</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min={0}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                          value={Number.isFinite(fabricSelection?.strokeWidth) ? fabricSelection?.strokeWidth : 0}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = Number(raw);
                            const v = Number.isFinite(parsed) ? parsed : 0;
                            window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail: { strokeWidth: v } }));
                            setFabricSelection((prev: any) => (prev ? { ...prev, strokeWidth: v } : prev));
                            if (!Number.isFinite(parsed)) e.target.value = '0';
                          }}
                        />
                        <input
                          type="color"
                          className="w-12 h-10 border border-gray-300 rounded"
                          value={fabricSelection?.stroke || '#000000'}
                          onChange={(e) => {
                            const c = e.target.value;
                            window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail: { stroke: c } }));
                            setFabricSelection((prev: any) => (prev ? { ...prev, stroke: c } : prev));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Text Effects */}
                {/* Text Effects */}
                {(fabricSelection?.type === 'IText' || fabricSelection?.type === 'text' || fabricSelection?.type === 'textbox') && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Text Effects</label>
                      {(() => {
                        let effectInfo = getEffectInfo(fabricSelection);
                        let effectType = effectInfo.effectType; 
                        const handleEffectChange = (val: string) => {
                          let detail: any = { effectType: val };
                          if (val === 'warp') {
                            detail.gridSize = 4
                          } else if (val === 'spline') {
                            detail.splineGridSize = 3;
                          }
                          console.log(detail)
                          window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail }));
                          setFabricSelection((prev: any) => ({ ...prev, ...detail, effectType: val }));
                        };

                        return (
                          <div className="flex flex-col space-y-2 text-sm">
                            <select
                              value={effectType}
                              onChange={(e) => handleEffectChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="none">None</option>
                              <option value="warp">Deformation</option>
                              <option value="spline">Spline Warp</option>
                              <option value="bezier">Bezier Curve</option>
                              <option value="circular">Circular Text</option>
                              <option value="extrude">3D Effect</option>
                              <option value="dots">Dot Matrix</option>
                              <option value="multicolor">Multi Color</option>
                              <option value="liquidwave">Liquid wave</option>
                            </select>
                            
                            {effectType === 'circular' && (
                                <div className="pl-6 mt-2 space-y-2">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Rotation Radius: {fabricSelection?.circularRadius || 60}</label>
                                    <input
                                      type="range"
                                      min="50"
                                      max="400"
                                      step="1"
                                      className="w-full"
                                      value={fabricSelection?.circularRadius || 60}
                                      onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const detail = {
                                            effectType: "circular",
                                            circularRadius: val,
                                            circularRotations: fabricSelection?.circularRotations || 2,
                                            circularAngle: fabricSelection?.circularAngle || 0.79
                                        };
                                        window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail }));
                                        setFabricSelection((prev: any) => (prev ? { ...prev, ...detail } : prev));
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Rotations: {fabricSelection?.circularRotations || 2}</label>
                                    <input
                                      type="range"
                                      min="0.1"
                                      max="5"
                                      step="0.1"
                                      className="w-full"
                                      value={fabricSelection?.circularRotations || 2}
                                      onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const detail = {
                                            effectType: "circular",
                                            circularRadius: fabricSelection?.circularRadius || 60,
                                            circularRotations: val,
                                            circularAngle: fabricSelection?.circularAngle || 0.79
                                        };
                                        window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail }));
                                        setFabricSelection((prev: any) => (prev ? { ...prev, ...detail } : prev));
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Initial Angle: {fabricSelection?.circularAngle || 0.79}</label>
                                    <input
                                      type="range"
                                      min="0"
                                      max="6.28"
                                      step="0.01"
                                      className="w-full"
                                      value={fabricSelection?.circularAngle || 0.79}
                                      onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const detail = {
                                            effectType: "circular",
                                            circularRadius: fabricSelection?.circularRadius || 60,
                                            circularRotations: fabricSelection?.circularRotations || 2,
                                            circularAngle: val
                                        };
                                        window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail }));
                                        setFabricSelection((prev: any) => (prev ? { ...prev, ...detail } : prev));
                                      }}
                                    />
                                  </div>
                                </div>
                            )}
                            
                            {effectType === 'liquidwave' && (
                                <div className="pl-6 mt-2 space-y-2">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Liquid Radius: {fabricSelection?.liquidRadius || 430}</label>
                                    <input
                                      type="range"
                                      min="100"
                                      max="800"
                                      step="10"
                                      className="w-full"
                                      value={fabricSelection?.liquidRadius || 430}
                                      onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const detail = {
                                            effectType: "liquidwave",
                                            liquidRadius: val,
                                            liquidStrength: fabricSelection?.liquidStrength || 0.12,
                                            liquidAngle: fabricSelection?.liquidAngle || 10
                                        };
                                        window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail }));
                                        setFabricSelection((prev: any) => (prev ? { ...prev, ...detail } : prev));
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Flow Strength: {fabricSelection?.liquidStrength || 0.12}</label>
                                    <input
                                      type="range"
                                      min="0.01"
                                      max="0.3"
                                      step="0.01"
                                      className="w-full"
                                      value={fabricSelection?.liquidStrength || 0.12}
                                      onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const detail = {
                                            effectType: "liquidwave",
                                            liquidRadius: fabricSelection?.liquidRadius || 430,
                                            liquidStrength: val,
                                            liquidAngle: fabricSelection?.liquidAngle || 10
                                        };
                                        window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail }));
                                        setFabricSelection((prev: any) => (prev ? { ...prev, ...detail } : prev));
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Noise Strength: {fabricSelection?.liquidAngle || 10}</label>
                                    <input
                                      type="range"
                                      min="1"
                                      max="30"
                                      step="1"
                                      className="w-full"
                                      value={fabricSelection?.liquidAngle || 10}
                                      onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const detail = {
                                            effectType: "liquidwave",
                                            liquidRadius: fabricSelection?.liquidRadius || 430,
                                            liquidStrength: fabricSelection?.liquidStrength || 0.12,
                                            liquidAngle: val
                                        };
                                        window.dispatchEvent(new CustomEvent('giftmkr:updateActiveObject', { detail }));
                                        setFabricSelection((prev: any) => (prev ? { ...prev, ...detail } : prev));
                                      }}
                                    />
                                  </div>
                                </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Common Properties */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Rotation</label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => rotateBy(-45)}
                        className="p-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded transition-colors"
                        title="Rotate Left 45°"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                      </button>

                      <input
                        type="number"
                        value={fabricSelection?.angle ?? 0}
                        onChange={handleAngleInputChange}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                        placeholder="0"
                        min="0"
                        max="360"
                      />

                      <button
                        onClick={() => rotateBy(45)}
                        className="p-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded transition-colors"
                        title="Rotate Right 45°"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {/* 用户添加元素的属性 */}
        {selectedElement && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Element</span>
              <div className="flex space-x-2">
                <button
                  onClick={() => onToggleElementVisibility(selectedElement)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  {elements.find(el => el.id === selectedElement)?.visible ? (
                    <EyeIcon className="w-4 h-4" />
                  ) : (
                    <EyeSlashIcon className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => onDeleteElement(selectedElement)}
                  className="p-1 hover:bg-gray-100 rounded text-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

          </div>
        )}

        {/* Product Options */}
        {/* <div className="mt-8 pt-8 border-t border-gray-200">
          <h4 className="font-semibold mb-4">Product Options</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Size</label>
              <select
                value={selectedSize}
                onChange={(e) => onSelectedSizeChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {product.sizes.map(size => (
                  <option key={size.id} value={size.id}>
                    {size.name} - ${size.price}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div> */}

      </div>

      {/* AI Modal */}
      <AIModal
        isOpen={isAIModalOpen}
        onClose={() => {
          setIsAIModalOpen(false);
          setSelectedAICode(undefined);
        }}
        selectedLayerId={selectedLayerId}
        selectedLayerUrl={selectedLayerUrlForModal}
        selectedAICode={selectedAICode}
        onGenerateAI={handleAIGenerate}
        onAIGenerated={handleAIGenerated}
      />
    </div>
  );
};

export default PropertiesPanel;
// 获取文件扩展名的辅助函数
const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return '.jpg'; // 默认扩展名
  }
  return filename.substring(lastDotIndex);
};
